import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import API from '../../../API';
import Item from './Item'
import Grid from '@mui/material/Grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ProgressOverlay from '../../overlays/ProgressOverlay';
import SubcategoryPanel from './SubcategoryPanel';
import BreadCrumbs from '../BreadCrumbs';
import ShippingNotice from '../ShippingNotice';
import { TENANTS, getTenantById } from '../../../tenants';

const SEARCH_TENANT_IDS = TENANTS.map((t) => t.id);

const Shop = () => {
  let { categoryId } = useParams<{categoryId: string}>();
  let { searchText } = useParams<{searchText: string}>();
  let { itemId } = useParams<{itemId: string}>();

  const [loading, setLoading] = useState<boolean>(false)
  const [items, setItems] = useState<IItem[]>([])
  const [currentCategoryId, setCurrentCategoryId] = useState<string|undefined>(undefined);
  // Tenant filter for search results. 'all' = show every tenant.
  const [searchTenant, setSearchTenant] = useState<string>('all');

  // We only want to refresh the item data in certain circumstances
  const refreshRequired = !itemId && (
    !currentCategoryId ||
    ((currentCategoryId !== categoryId) && (currentCategoryId !== searchText)));

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      try {
        if (searchText) {
          const items = await API.searchItemsAcrossTenants(searchText, SEARCH_TENANT_IDS);
          setItems(items);
          setCurrentCategoryId(searchText);
        }
        else {
          const items = await API.getItemsByCategory(categoryId);
          // FIXME: We're going to want to allow selectable sorts here!
          items.sort((i1, i2) => {
            const s1 = Object.keys(i1.inventory).reduce((sum, key) => sum + i1.inventory[key].count, 0);
            const s2 = Object.keys(i2.inventory).reduce((sum, key) => sum + i2.inventory[key].count, 0);
            if (s1 > 0 && s2 <= 0) {
                return -1;
            }
            if (s2 > 0 && s1 <= 0) {
                return 1;
            }
            return i1.name.localeCompare(i2.name);
          });

          setItems(items);
          setCurrentCategoryId(categoryId);
        }
      }
      catch (err: any) {
        console.error(err.message);
      }
      setLoading(false);
    }

    if (refreshRequired) {
      fetchItems();
    }
  }, [searchText, categoryId]);

  if (searchText) {
    const counts: Record<string, number> = {};
    items.forEach((i) => {
      if (i.tenant) counts[i.tenant] = (counts[i.tenant] ?? 0) + 1;
    });

    const visibleItems = searchTenant === 'all'
      ? items
      : items.filter((i) => i.tenant === searchTenant);

    const sections = searchTenant === 'all'
      ? TENANTS
          .map((t) => ({ tenantId: t.id, items: items.filter((i) => i.tenant === t.id) }))
          .filter((g) => g.items.length > 0)
      : [{ tenantId: searchTenant, items: visibleItems }];

    return (
      <ProgressOverlay enabled={loading}>
        <div className='infobar'>
          <BreadCrumbs categoryId={categoryId}/>
          <div className='spacer'/>
          <ShippingNotice/>
        </div>
        <div style={{ padding: '0.5rem 1rem', display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={searchTenant}
            exclusive
            size="small"
            onChange={(_e, v) => { if (v !== null) setSearchTenant(v as string); }}
            aria-label="Filter search results by shop"
          >
            <ToggleButton value="all">All ({items.length})</ToggleButton>
            {TENANTS.map((t) => (
              <ToggleButton key={t.id} value={t.id}>
                {t.label} ({counts[t.id] ?? 0})
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>
        {!loading && visibleItems.length === 0 && (
          <Typography variant="body2" sx={{ padding: '1rem', textAlign: 'center', color: '#64748b' }}>
            No items match "{searchText}".
          </Typography>
        )}
        {sections.map((section) => {
          const label = getTenantById(section.tenantId)?.label ?? section.tenantId;
          return (
            <div key={section.tenantId}>
              {searchTenant === 'all' && (
                <Typography
                  variant="h6"
                  sx={{ padding: '0.75rem 1rem 0.25rem', fontSize: '1.05rem' }}
                >
                  {label}
                </Typography>
              )}
              <Grid container justifyContent="center" spacing={2} className="items">
                {section.items.map((item: IItem) => (
                  <Grid key={`${item.tenant ?? ''}-${item.id}`} item>
                    <Item item={item} />
                  </Grid>
                ))}
              </Grid>
            </div>
          );
        })}
      </ProgressOverlay>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <>
        <div className='infobar'>
          <BreadCrumbs categoryId={categoryId}/>
          <div className='spacer'/>
          <ShippingNotice/>
        </div>
        <SubcategoryPanel categoryId={categoryId}/>
      </>
    )
  }

  return <>
    <ProgressOverlay enabled={loading}>
      <div className='infobar'>
        <BreadCrumbs categoryId={categoryId}/>
        <div className='spacer'/>
        <ShippingNotice/>
      </div>
      <Grid container justifyContent="center" spacing={2} className="items">
        {items.map((item: IItem) => (
          <Grid key={item.id} item>
            <Item
              key={item.id}
              item = {item}
            />
          </Grid>
        ))}

      </Grid>
    </ProgressOverlay>
  </>;
}

export default Shop
